import { useRouter } from 'next/router'
import { KeyboardEvent } from 'react'

/**
 * Custom hook for handling search bar key events and navigation.
 *
 * @returns {object} An object containing the handleSearchKeyDown callback.
 * @example
 * const { handleSearchKeyDown } = useSearchBarUtil()
 * <InputSearch handleSearchKeyDown={handleSearchKeyDown} />
 */
export function useSearchBarUtil(): {
  handleSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
} {
  const router = useRouter()

  /**
   * Handles the key down event for the search input.
   * Navigates to the search page if Enter is pressed and the search term is valid.
   *
   * @param {KeyboardEvent<HTMLInputElement>} event - The keyboard event from the input.
   */
  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      const searchTerm: string = event.target.value
      if (searchTerm.trim() !== '' && searchTerm.length >= 3) {
        const pathParts: string[] = router.asPath.split('/')
        const productId: string | null =
          pathParts[1] === 'products' ? pathParts[2] : null
        const query: { searchTermQuery: string; productId?: string } = {
          searchTermQuery: searchTerm,
        }
        if (productId) {
          query.productId = productId
        }
        router.push(
          {
            pathname: '/search',
            query,
          },
          undefined,
          { shallow: true }
        )
      }
    }
  }

  return {
    handleSearchKeyDown,
  }
}
